import type {
  FunnelABTestDecisionRow,
  FunnelABTestVariantScore,
  StatTier,
} from './types';

export type DecisionVerdict = 'ship' | 'dont_ship' | 'keep_collecting';

export interface DecisionVerdictResult {
  verdict: DecisionVerdict;
  label: 'Ship' | "Don't ship" | 'Keep collecting';
  /** One-sentence plain-language rationale shown next to the verdict. */
  reason: string;
  /** The variant the verdict is about (winner or current leader), if any. */
  leadingVariantId: string | null;
  /** Probability to beat control for the leading variant on the primary metric. */
  probabilityToBeatControl: number | null;
  /** Expected loss per user for the leading variant on the primary metric. */
  expectedLossPerUser: number | null;
  guardrailBreached: boolean;
  sampleRatioMismatch: boolean;
  allocationMetadataUnknown: boolean;
  deliveryOnly: boolean;
  /** Spec D P6 stop valve (server truth passed through): pause follow-up actions. */
  stopValve: boolean;
  /** Server-computed tier of the leading variant's primary metric, if qualified. */
  leadingTier: StatTier | null;
}

const SRM_P_VALUE_THRESHOLD = 0.001;

/** Names-first variant label (QW1 AC-704): snapshot name over bare id. */
function variantLabel(row: FunnelABTestDecisionRow, variantId: string): string {
  const score = row.scores.find(candidate => candidate.variantId === variantId);
  return score?.variantName ?? variantId;
}

function findLeadingVariant(row: FunnelABTestDecisionRow): FunnelABTestVariantScore | null {
  if (row.winnerVariantId) {
    return row.scores.find(score => score.variantId === row.winnerVariantId) ?? null;
  }
  const candidates = row.scores.filter(score =>
    score.variantId !== row.controlVariantId &&
    score.comparisonToControl[row.primaryMetric]?.probabilityToBeatControl != null,
  );
  if (!candidates.length) return null;
  return candidates.reduce((best, current) => {
    const bestProbability = best.comparisonToControl[row.primaryMetric]?.probabilityToBeatControl ?? -1;
    const currentProbability = current.comparisonToControl[row.primaryMetric]?.probabilityToBeatControl ?? -1;
    return currentProbability > bestProbability ? current : best;
  });
}

/**
 * Decision-first verdict mapping (confirmed product policy):
 *   winner_declared / ready_to_choose → Ship
 *   guardrail breach or invalid (incl. SRM) → Don't ship
 *   collecting / inconclusive → Keep collecting
 * Guardrail/validity problems take precedence over a positive status.
 */
export function resolveDecisionVerdict(row: FunnelABTestDecisionRow): DecisionVerdictResult {
  const guardrailBreached = row.scores.some(score => score.guardrails.some(guardrail => guardrail.breached));
  // Spec D D1: the SERVER's srmOk wins; the p-value threshold recompute stays
  // only as the fallback for payloads that predate the qualification fields.
  const srmStatus = row.srm?.status;
  const sampleRatioMismatch = srmStatus
    ? srmStatus === 'failed'
    : row.srmOk !== undefined
      ? !row.srmOk
      : row.sampleRatioMismatchPValue !== null && row.sampleRatioMismatchPValue < SRM_P_VALUE_THRESHOLD;
  const allocationMetadataUnknown = srmStatus === 'unknown';
  const deliveryOnly = srmStatus === 'not_applicable' || row.status === 'delivery_only';
  const leading = findLeadingVariant(row);
  const comparison = leading?.comparisonToControl[row.primaryMetric];

  const base = {
    leadingVariantId: leading?.variantId ?? null,
    probabilityToBeatControl: comparison?.probabilityToBeatControl ?? null,
    expectedLossPerUser: comparison?.expectedLossPerUser ?? null,
    guardrailBreached,
    sampleRatioMismatch,
    allocationMetadataUnknown,
    deliveryOnly,
    stopValve: row.stopValve ?? (sampleRatioMismatch || allocationMetadataUnknown),
    leadingTier: leading?.qualification?.tier ?? null,
  };

  if (deliveryOnly) {
    return {
      ...base,
      verdict: 'keep_collecting',
      label: 'Keep collecting',
      reason: 'Single-variant delivery is active. It is not an experiment, so SRM and winner decisions do not apply.',
    };
  }

  if (row.status === 'invalid') {
    return {
      ...base,
      verdict: 'dont_ship',
      label: "Don't ship",
      reason: sampleRatioMismatch
        ? 'Traffic split does not match the configured weights (sample ratio mismatch) — results are not trustworthy.'
        : allocationMetadataUnknown
          ? row.srm?.reasonCode === 'mixed_allocation_epoch'
            ? 'The selected window mixes multiple allocation epochs. Choose one epoch before making a decision.'
            : 'Immutable allocation metadata is unavailable. Decisions are paused until the epoch can be identified.'
          : 'The test setup is invalid, so its results cannot be trusted.',
    };
  }

  if (guardrailBreached) {
    const breach = row.scores
      .flatMap(score => score.guardrails)
      .find(guardrail => guardrail.breached);
    return {
      ...base,
      verdict: 'dont_ship',
      label: "Don't ship",
      reason: breach?.reason
        ? `A guardrail metric regressed: ${breach.reason}`
        : `A guardrail metric (${breach?.metric ?? 'unknown'}) regressed beyond the allowed threshold.`,
    };
  }

  if (row.status === 'winner_declared') {
    return {
      ...base,
      verdict: 'ship',
      label: 'Ship',
      reason: row.winnerVariantId
        ? `Variant ${variantLabel(row, row.winnerVariantId)} was declared the winner on ${describeMetric(row.primaryMetric)}.`
        : `A winner was declared on ${describeMetric(row.primaryMetric)}.`,
    };
  }

  if (row.status === 'ready_to_choose') {
    const probability = base.probabilityToBeatControl;
    return {
      ...base,
      verdict: 'ship',
      label: 'Ship',
      reason: leading && probability !== null
        ? `Variant ${leading.variantName ?? leading.variantId} has a ${Math.round(probability * 100)}% chance of beating control on ${describeMetric(row.primaryMetric)}.`
        : `There is enough evidence to choose a variant on ${describeMetric(row.primaryMetric)}.`,
    };
  }

  if (row.status === 'inconclusive') {
    return {
      ...base,
      verdict: 'keep_collecting',
      label: 'Keep collecting',
      reason: row.recommendation || 'Results are mixed — the lift does not translate into a profitable outcome yet.',
    };
  }

  return {
    ...base,
    verdict: 'keep_collecting',
    label: 'Keep collecting',
    reason: 'Not enough data yet to make a trustworthy call.',
  };
}

export function describeMetric(metric: FunnelABTestDecisionRow['primaryMetric']): string {
  switch (metric) {
    case 'first_to_second_rate': return 'first-to-second screen rate';
    case 'purchase_conversion_rate': return 'purchase conversion';
    case 'revenue_per_assigned_user': return 'revenue per user';
    case 'profit_per_assigned_user': return 'profit per user';
    default: return metric;
  }
}
